import { createContext, useContext } from 'react'

/**
 * Cross-component handle for the TASK-391 geolocation feature.
 *
 * The `RouteCard` "my location" button lives in two host surfaces
 * (`MobileRouteCardOverlay` phone overlay + `StationCardOrDetailsRouter`
 * tablet sidebar). Both read `onMyLocation` from this context so the
 * geolocation wiring is owned in exactly one place (`GeolocationController`,
 * mounted in `AppShell`).
 */
export interface GeolocationContextValue {
  /** Invoked when the user taps the "my location" button. */
  readonly onMyLocation: () => void
  /**
   * `true` from the moment the user taps "my location" until the fix
   * resolves (or fails / times out at 12 s). The my-location button host
   * surfaces observe this and show a spinner on the button (and block
   * re-taps) so the request reads as in-progress (TASK-391 §3.3).
   */
  readonly isResolvingMyLocation: boolean
  /**
   * Canvas position of the station the user-location dot should mark,
   * or `null` when the dot is hidden (no fix, outside the UK, or beyond
   * the distance threshold). Updates live as the user moves (TASK-391
   * §3.6). MetroMapCanvas reads this to render `UserLocationDotOverlay`.
   */
  readonly dotTarget: { readonly canvasX: number; readonly canvasY: number } | null
}

export const GeolocationContext = createContext<GeolocationContextValue | null>(null)

/**
 * Reads the geolocation handle. Returns `null` when no
 * `GeolocationController` is mounted (e.g. isolated component tests) —
 * callers treat that as "geolocation unavailable" and omit the button.
 */
export function useGeolocation(): GeolocationContextValue | null {
  return useContext(GeolocationContext)
}
